import {
  GameDifficulty,
  GameType,
  TrackingPageGameBlock,
} from "@redotech/redo-model/order-tracking/tracking-page-definition";
import { KAPLAYCtx } from "kaplay";
import { CustomSprite, TrackingPageGame, VictoryState } from "../game";
import { GameCompleteCallback, GameControls } from "../game-controls";
import {
  extendHillTerrain,
  extendMountainTerrain,
  generateBackgroundTerrain,
  renderBackgroundTerrain,
} from "./background-terrain";
import { applyCameraTransform, updateCamera } from "./camera";
import {
  CAMERA,
  GAME,
  NPC,
  PHYSICS,
  PLAYER,
  TERRAIN,
  TINY_PACKAGE_ASSETS_URL,
} from "./constants";
import {
  initializeDayNightCycle,
  updateDayNightCycle,
} from "./day-night-cycle";
import { renderNpcTrail, updateNPC } from "./npc";
import {
  applyDamping,
  applyDiveForce,
  applyGravity,
  applyVelocityConstraints,
  handleTerrainCollision,
  updatePlayerAngle,
  updatePlayerPosition,
  updateTrail,
} from "./physics";
import {
  renderFinishLineMarker,
  renderPlayer,
  renderSky,
  renderTerrain,
  renderTrail,
} from "./rendering";
import {
  renderSpeedLineParticles,
  updateSpeedLineParticles,
} from "./speed-lines";
import {
  cleanupOldTerrain,
  extendTerrain,
  generateInitialTerrain,
  getTerrainY,
} from "./terrain";
import type {
  Camera,
  NPC as NPCType,
  Player,
  SpeedLineParticle,
} from "./types";
import { createUIElements, resizeUIElements, updateUIElements } from "./ui";

export const tinyPackageGame: TrackingPageGame = {
  gameType: GameType.TINY_PACKAGE,
  initializeGame: initializeTinyWingsGame,
  strings: {
    victory: "You won!",
    failure: "You lost!",
    credits:
      '"Airship Serenity" Kevin MacLeod (incompetech.com)\nLicensed under Creative Commons: By Attribution 4.0 License',
    instructions:
      "Click the screen or press space to dive down.\n Race your opponent to the finish line!",
  },
};

export function initializeTinyWingsGame(
  k: KAPLAYCtx,
  onGameComplete: GameCompleteCallback,
  blockProps: TrackingPageGameBlock,
  playerSprite?: CustomSprite | null,
  npcSprite?: CustomSprite | null,
): GameControls {
  let difficulty: GameDifficulty = "medium";
  if (blockProps.gameType === GameType.TINY_PACKAGE) {
    difficulty = blockProps.gameSettings.difficulty || "medium";
  }

  let playerSpriteSize =
    blockProps.gameType === GameType.TINY_PACKAGE
      ? blockProps.gameSettings.playerSpriteSize || 32
      : 32;
  let playerRadius = playerSpriteSize / 2 / PHYSICS.PTM_RATIO;

  let npcSpriteSize =
    blockProps.gameType === GameType.TINY_PACKAGE
      ? blockProps.gameSettings.npcSpriteSize || 32
      : 32;
  let npcRadius = npcSpriteSize / 2 / PHYSICS.PTM_RATIO;

  const npcSpeed = NPC.GROUND_SPEED[difficulty];

  let spriteVerticalOffset = 0;
  let npcSpriteVerticalOffset = 0;

  if (playerSprite) {
    k.loadSprite("player", playerSprite.url);
    spriteVerticalOffset = playerSprite.verticalOffset;
  } else {
    k.loadSprite("player", "https://play.kaplayjs.com/sprites/bean.png");
    spriteVerticalOffset = 0;
  }

  if (npcSprite) {
    k.loadSprite("mark", npcSprite.url);
    npcSpriteVerticalOffset = npcSprite.verticalOffset;
  } else {
    k.loadSprite("mark", "https://play.kaplayjs.com/sprites/mark.png");
    npcSpriteVerticalOffset = 0;
  }

  k.loadSound("music", `${TINY_PACKAGE_ASSETS_URL}/music.mp3`);
  k.loadSound("whoosh", `${TINY_PACKAGE_ASSETS_URL}/whoosh.mp3`);
  k.loadSound("hurt", `${TINY_PACKAGE_ASSETS_URL}/hurt.mp3`);

  const music = k.play("music", { loop: true, volume: 0.3 });
  music.stop();

  const whoosh = k.play("whoosh", { loop: false, volume: 0.3 });
  whoosh.stop();

  const hurt = k.play("hurt", { loop: false, volume: 0.5 });
  hurt.stop();

  const canvasWidth = k.width();
  const canvasHeight = k.height();
  const baselineY = canvasHeight * CAMERA.BASELINE_FACTOR;

  // Initialize game state
  const terrain = generateInitialTerrain(canvasHeight);
  const backgroundTerrain = generateBackgroundTerrain(canvasHeight);
  const dayNight = initializeDayNightCycle();
  const speedLineParticles: SpeedLineParticle[] = [];
  const player: Player = {
    x: GAME.START_X,
    y: getTerrainY(GAME.START_X, terrain),
    vx: 0,
    vy: 0,
    onGround: false,
    angle: 0,
    trail: [],
  };

  const npc: NPCType = {
    x: GAME.START_X - NPC.START_OFFSET,
    y: getTerrainY(GAME.START_X - NPC.START_OFFSET, terrain),
    vx: 0,
    vy: 0,
    onGround: false,
    angle: 0,
    trail: [],
  };
  const camera: Camera = {
    x: 0,
    scale: 1.0,
    shakeTime: 0,
    shakeIntensity: 0,
    initialShakeDuration: 0,
  };

  // Create fixed UI elements
  const uiElements = createUIElements(k);

  let gameEnded = false;
  const gameStarted = true;
  let isDiving = false;
  let frameCount = 0;
  let lastSpeedLineSpawnTime = 0;
  let whooshPlayed = false;

  if (playerSprite) {
    uiElements.playerRaceIcon.use(k.sprite("player"));
  }

  if (npcSprite) {
    uiElements.npcRaceIcon.use(k.sprite("mark"));
  }

  music.play();

  player.vx = PLAYER.WAKE_IMPULSE_X;
  player.vy = PLAYER.WAKE_IMPULSE_Y;

  function startDive() {
    if (gameEnded) return;
    isDiving = true;
  }

  function stopDive() {
    isDiving = false;
    whooshPlayed = false;
  }

  k.onUpdate(() => {
    const dt = k.dt();
    frameCount++;

    updateDayNightCycle(dayNight, dt);

    if (frameCount % 60 === 0) {
      const lastFarPoint =
        backgroundTerrain.farLayer.points[
          backgroundTerrain.farLayer.points.length - 1
        ];
      if (lastFarPoint && lastFarPoint.x < camera.x + 3000) {
        extendMountainTerrain(backgroundTerrain.farLayer.points, canvasHeight);
      }

      const lastNearPoint =
        backgroundTerrain.nearLayer.points[
          backgroundTerrain.nearLayer.points.length - 1
        ];
      if (lastNearPoint && lastNearPoint.x < camera.x + 3000) {
        extendHillTerrain(backgroundTerrain.nearLayer.points, canvasHeight);
      }
    }

    if (gameEnded || !gameStarted) {
      whoosh.volume = 0;
      music.volume = 0;
      hurt.volume = 0;
      music.stop();
    }

    applyGravity(player, dt);
    applyDamping(player, dt);
    applyDiveForce(player, isDiving, dt);
    updatePlayerPosition(player, dt);
    handleTerrainCollision(player, terrain, camera, dt, hurt, playerRadius);
    applyVelocityConstraints(player);
    updatePlayerAngle(player);
    updateTrail(player, spriteVerticalOffset);

    updateNPC(npc, terrain, dt, npcSpeed, npcSpriteVerticalOffset, npcRadius);

    lastSpeedLineSpawnTime = updateSpeedLineParticles(
      speedLineParticles,
      dt,
      player.x,
      player.y,
      player.vx,
      player.vy,
      isDiving,
      lastSpeedLineSpawnTime,
      spriteVerticalOffset,
    );

    if (isDiving && speedLineParticles.length > 0 && !whooshPlayed) {
      whoosh.play();
      whooshPlayed = true;
    }

    updateCamera(camera, player, baselineY, canvasWidth, canvasHeight, dt);
    applyCameraTransform(k, camera, player, baselineY);

    cleanupOldTerrain(terrain, camera.x, frameCount);

    const terrainEndX = terrain[terrain.length - 1]?.x || 0;
    const distanceToEnd = terrainEndX - player.x;
    const bufferDistance = TERRAIN.HILL_WIDTH * 20;

    if (distanceToEnd < bufferDistance) {
      extendTerrain(terrain, canvasHeight);
    }

    const distanceTraveled = player.x - GAME.START_X;
    const npcDistanceTraveled = npc.x - (GAME.START_X - NPC.START_OFFSET);

    if (distanceTraveled >= GAME.TARGET_DISTANCE && !gameEnded) {
      gameEnded = true;
      onGameComplete({ victoryState: VictoryState.VICTORY });
    } else if (npcDistanceTraveled >= GAME.TARGET_DISTANCE && !gameEnded) {
      gameEnded = true;
      onGameComplete({ victoryState: VictoryState.FAILURE });
    }
  });

  k.onDraw(() => {
    renderSky(k, camera, dayNight);

    renderBackgroundTerrain(k, backgroundTerrain, camera);

    renderTerrain(k, terrain, camera);

    renderFinishLineMarker(k, camera, terrain);

    renderTrail(k, player.trail);
    renderNpcTrail(k, npc.trail, npcRadius);
    renderSpeedLineParticles(k, speedLineParticles, camera);
    renderPlayer(k, player, "player", spriteVerticalOffset, playerRadius);
    renderPlayer(k, npc, "mark", npcSpriteVerticalOffset, npcRadius);

    updateUIElements(uiElements, k, player, npc, gameStarted);
  });

  k.onKeyPress("space", startDive);
  k.onKeyRelease("space", stopDive);
  k.onMousePress(startDive);
  k.onMouseRelease(stopDive);

  const updateSprites = (
    playerSprite?: CustomSprite | null,
    npcSprite?: CustomSprite | null,
    newPlayerSpriteSize?: number,
    newNpcSpriteSize?: number,
  ) => {
    if (playerSprite) {
      k.loadSprite("player", playerSprite.url);
      spriteVerticalOffset = playerSprite.verticalOffset;
      if (uiElements.playerRaceIcon) {
        uiElements.playerRaceIcon.use(k.sprite("player"));
      }
    }

    if (npcSprite) {
      k.loadSprite("mark", npcSprite.url);
      npcSpriteVerticalOffset = npcSprite.verticalOffset;
      if (uiElements.npcRaceIcon) {
        uiElements.npcRaceIcon.use(k.sprite("mark"));
      }
    }

    // Update sprite sizes and physics radii if provided
    if (newPlayerSpriteSize !== undefined) {
      playerSpriteSize = newPlayerSpriteSize;
      playerRadius = playerSpriteSize / 2 / PHYSICS.PTM_RATIO;
    }

    if (newNpcSpriteSize !== undefined) {
      npcSpriteSize = newNpcSpriteSize;
      npcRadius = npcSpriteSize / 2 / PHYSICS.PTM_RATIO;
    }
  };

  const gameControls: GameControls = {
    moveUp: startDive,
    moveDown: startDive,
    moveLeft: startDive,
    moveRight: startDive,
    stopMovement: stopDive,
    updateSprites,
    onResize: () => resizeUIElements(uiElements, k),
  };

  return gameControls;
}
